import React, { useState, useEffect } from 'react';
import { Select, Space, Input } from 'antd';
import { useReactFlow, useStore } from '@xyflow/react';
import { INTERACTION_TYPES } from 'util/constants';
import { COLLECTIBLE_AUDIOS } from '@ozoneuniverse/mfe-core/dist/components/managers/timeline/data/collectibleAudios';
import styles from '../../event-timeline.module.css';

const { Option } = Select;

type AudioItemT = {
  name: string;
  url: string;
};

interface CollectibleParamsProps {
  nodeId: string;
  selectableAssets: any[];
}

interface CollectibleParams {
  interactionType: INTERACTION_TYPES;
  targetId: string;
  audioType?: string;
  animationType?: string;
  title?: string;
  description?: string;
}

const CollectibleParams: React.FC<CollectibleParamsProps> = ({
  nodeId,
  selectableAssets,
}) => {
  const { setNodes } = useReactFlow();
  const node = useStore((state) => state.nodes.find((n) => n.id === nodeId));
  const collectible = node?.data ?? ({} as any);
  const defaultParams: CollectibleParams = {
    interactionType: INTERACTION_TYPES.COLLISION,
    targetId: '',
    audioType: 'default',
    animationType: 'fadeOut',
    title: '',
    description: '',
  };
  const collectibleAudios = Object.entries(COLLECTIBLE_AUDIOS) as [
    string,
    AudioItemT
  ][];
  const [params, setParams] = useState<CollectibleParams>(
    collectible.params || defaultParams
  );
  const [objectId, setObjectId] = useState<string>(
    collectible.params?.targetId || ''
  );
  const [audioType, setAudioType] = useState<string>(
    collectible.params?.audioType || 'default'
  );
  const [animationType, setAnimationType] = useState<string>(
    collectible.params?.animationType || 'fadeOut'
  );
  const [title, setTitle] = useState<string>(collectible.params?.title || '');
  const [description, setDescription] = useState<string>(
    collectible.params?.description || ''
  );

  const updateNodeData = (params: CollectibleParams) => {
    setNodes((nodes) =>
      nodes.map((n) =>
        n.id === nodeId
          ? {
              ...n,
              data: {
                ...n.data,
                params,
              },
            }
          : n
      )
    );
  };

  function onChangeParams(params: CollectibleParams) {
    setParams(params);
    updateNodeData(params);
  }

  function onChangeObjectId(value: string) {
    setObjectId(value);
    onChangeParams({ ...params, targetId: value });
  }

  function onChangeAudioType(value: string) {
    setAudioType(value);
    onChangeParams({ ...params, audioType: value });
  }

  function onChangeAnimationType(value: string) {
    setAnimationType(value);
    onChangeParams({ ...params, animationType: value });
  }

  function onChangeTitle(value: string) {
    setTitle(value);
    onChangeParams({ ...params, title: value });
  }

  function onChangeDescription(value: string) {
    setDescription(value);
    onChangeParams({ ...params, description: value });
  }

  useEffect(() => {
    setParams(collectible.params || defaultParams);
    setObjectId(collectible.params?.targetId || '');
    setAudioType(collectible.params?.audioType || 'default');
    setAnimationType(collectible.params?.animationType || 'fadeOut');
    setTitle(collectible.params?.title || '');
    setDescription(collectible.params?.description || '');
  }, [node?.id]);

  return (
    <>
      <div className={styles.parameterContainer}>
        <Space direction='vertical' style={{ width: '100%' }}>
          <span className={styles.label}>Title:</span>
          <Input
            value={title}
            onChange={(e) => onChangeTitle(e.target.value)}
            placeholder='Enter collectible title'
          />
        </Space>
      </div>

      <div className={styles.parameterContainer}>
        <Space direction='vertical' style={{ width: '100%' }}>
          <span className={styles.label}>Description:</span>
          <Input.TextArea
            value={description}
            onChange={(e) => onChangeDescription(e.target.value)}
            placeholder='Enter collectible description'
            rows={3}
          />
        </Space>
      </div>

      <div className={styles.parameterContainer}>
        <Space direction='vertical' style={{ width: '100%' }}>
          <span className={styles.label}>Target ID:</span>
          {selectableAssets?.length > 0 ? (
            <Select
              value={objectId}
              onChange={onChangeObjectId}
              placeholder='Select object'
              style={{ width: '100%' }}
            >
              {selectableAssets?.map((asset) => (
                <Option
                  key={
                    asset.props.asset.id || asset.props.asset.ref.current?.uuid
                  }
                  value={
                    asset.props.asset.id || asset.props.asset.ref.current?.uuid
                  }
                >
                  {asset.props.asset.name}
                </Option>
              ))}
            </Select>
          ) : (
            <Input
              value={params.targetId}
              onChange={(e) =>
                onChangeParams({ ...params, targetId: e.target.value })
              }
              placeholder='Enter Object ID'
            />
          )}
        </Space>
      </div>

      <div className={styles.parameterContainer}>
        <Space direction='vertical' style={{ width: '100%' }}>
          <span className={styles.label}>Audio on Find:</span>
          <Select
            value={audioType}
            onChange={onChangeAudioType}
            placeholder='Select audio'
            style={{ width: '100%' }}
          >
            {collectibleAudios.map(([key, audio]) => (
              <Option key={key} value={key}>
                {audio.name}
              </Option>
            ))}
          </Select>
        </Space>
      </div>

      <div className={styles.parameterContainer}>
        <Space direction='vertical' style={{ width: '100%' }}>
          <span className={styles.label}>Animation on Find:</span>
          <Select
            value={animationType}
            onChange={onChangeAnimationType}
            placeholder='Select animation'
            style={{ width: '100%' }}
          >
            <Option value='none'>None</Option>
            <Option value='fadeOut'>Fade Out</Option>
            <Option value='rotate'>Rotate</Option>
            <Option value='flyUp'>Fly Up</Option>
          </Select>
        </Space>
      </div>
    </>
  );
};

export default CollectibleParams;
